import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import SellerSalesChart from '../components/SellerSalesChart';
import SellerRecentSales from '../components/SellerRecentSales';
import SellerSalesStats from '../components/SellerSalesStats';
import SellerMonthlyExpenses from '../components/SellerMonthlyExpenses';
import ChatWidget from '../components/ChatWidget'; // Asistente IA
// SellerTopProducts ha sido eliminado
import '../styles/Dashboard.css';
import '../styles/Modal.css';
import '../styles/SellerDashboard.css';

const SellerDashboard = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [modalType, setModalType] = useState('error');
  
  // Estados para el punto de venta asignado al vendedor y el negocio
  const [assignedPointOfSale, setAssignedPointOfSale] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [pointOfSaleLoading, setPointOfSaleLoading] = useState(false);
  
  // Estado para controlar qué sección se muestra
  const [activeSection, setActiveSection] = useState('dashboard');
  
  // Estados para estadísticas de ventas
  const [salesStats, setSalesStats] = useState(null);
  const [salesStatsLoading, setSalesStatsLoading] = useState(false);
  
  // Estado para ventas recientes
  const [recentSales, setRecentSales] = useState([]);
  const [recentSalesLoading, setRecentSalesLoading] = useState(false);
  
  // Estado para productos más vendidos
  const [topProducts, setTopProducts] = useState([]);
  const [topProductsLoading, setTopProductsLoading] = useState(false);

  // Fetch assigned point of sale for the seller
  const fetchAssignedPointOfSale = async () => {
    if (user && user.role === 'seller') {
      try {
        setPointOfSaleLoading(true);
        const token = localStorage.getItem('token');
        
        const response = await axios.get('/api/points-of-sale/assigned', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.data && response.data.pointsOfSale && response.data.pointsOfSale.length > 0) {
          // Asumimos que un vendedor solo está asignado a un punto de venta
          setAssignedPointOfSale(response.data.pointsOfSale[0]);
          
          // Guardamos la información del negocio
          if (response.data.businessInfo) {
            setBusinessInfo(response.data.businessInfo);
          }
          
          // Ya no necesitamos llamar a estas funciones aquí
          // Los componentes individuales se encargarán de obtener sus propios datos
        } else {
          console.log('No se encontró un punto de venta asignado para este vendedor');
        }
      } catch (error) {
        console.error('Error al obtener el punto de venta asignado:', error);
        setModalTitle('Error');
        setModalMessage('No se pudo obtener la información del punto de venta asignado. Por favor, contacte al administrador.');
        setModalType('error');
        setModalOpen(true);
      } finally {
        setPointOfSaleLoading(false);
      }
    }
  };

  // Ya no necesitamos estas funciones, los componentes individuales se encargan de obtener sus propios datos

  // Handlers para los botones del menú
  const handleRegistrarVentaClick = () => {
    navigate('/seller-sales-type');
  };

  const handleRegistrarGastoClick = () => {
    navigate('/register-expense');
  };

  const handleProductosClick = () => {
    navigate('/seller-products');
  };

  const handleClientesClick = () => {
    navigate('/seller-customers');
  };

  const handleReportesClick = () => {
    navigate('/seller-reports');
  };
  
  const handleDashboardClick = () => {
    setActiveSection('dashboard');
  };

  // Load data when component mounts
  useEffect(() => {
    if (user && user.role === 'seller') {
      fetchAssignedPointOfSale();
    } else if (user && user.role !== 'seller') {
      // Redirect to regular dashboard if not a seller
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Loading state
  if (loading || !user) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <>
    <div className="dashboard-container">
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        message={modalMessage}
        type={modalType}
      />
      
      <header className="dashboard-header">
        <h1>Dashboard de Vendedor</h1>
        <div className="user-info">
          {user && (
            <span className="welcome-message">
              Bienvenido, {user.full_name} | <button onClick={logout} className="logout-btn">Cerrar Sesión</button>
            </span>
          )}
        </div>
      </header>
      
      {/* Botones destacados para vendedores */}
      {assignedPointOfSale && (
        <div className="highlighted-actions">
          <button 
            onClick={handleRegistrarVentaClick} 
            className="action-button sales-button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg>
            Registrar Ventas
          </button>
          <button 
            onClick={handleRegistrarGastoClick} 
            className="action-button expenses-button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2"></rect>
              <line x1="2" y1="10" x2="22" y2="10"></line>
              <line x1="6" y1="14" x2="6" y2="18"></line>
              <line x1="12" y1="14" x2="12" y2="18"></line>
              <line x1="18" y1="14" x2="18" y2="18"></line>
            </svg>
            Registrar Gastos
          </button>
        </div>
      )}



      {/* Menú de navegación para vendedores (simplificado) */}
      {assignedPointOfSale && (
        <nav className="admin-menu">
          <ul className="admin-menu-list">
            <li className="admin-menu-item">
              <button 
                onClick={handleProductosClick} 
                className={`admin-menu-link ${activeSection === 'productos' ? 'active' : ''}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <path d="M16 10a4 4 0 0 1-8 0"></path>
                </svg>
                Productos
              </button>
            </li>
            <li className="admin-menu-item">
              <button onClick={handleClientesClick} className="admin-menu-link">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                Clientes
              </button>
            </li>
            <li className="admin-menu-item">
              <button onClick={handleReportesClick} className="admin-menu-link">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                  <line x1="2" y1="10" x2="22" y2="10"></line>
                  <line x1="6" y1="14" x2="6" y2="18"></line>
                  <line x1="12" y1="14" x2="12" y2="18"></line>
                  <line x1="18" y1="14" x2="18" y2="18"></line>
                </svg>
                Reportes
              </button>
            </li>
          </ul>
        </nav>
      )}

      <div className="dashboard-content">
        {assignedPointOfSale ? (
          <>
            {activeSection === 'dashboard' ? (
              <>
                {/* Sección superior con información del punto de venta y ventas recientes */}
                <div className="dashboard-top-section">
                  {/* Información del punto de venta */}
                  <div className="pos-info">
                    <h2>Punto de Venta Asignado</h2>
                    <p><strong>Nombre:</strong> {assignedPointOfSale.pos_name}</p>
                    <p><strong>Dirección:</strong> {assignedPointOfSale.pos_address}</p>
                    <p><strong>Estado:</strong> {assignedPointOfSale.pos_closed ? 'Cerrado' : 'Abierto'}</p>
                    {businessInfo && (
                      <div className="business-info">
                        <p><strong>Negocio:</strong> {businessInfo.bis_name}</p>
                        <p><strong>Teléfono:</strong> {businessInfo.bis_phone}</p>
                        <p><strong>País:</strong> {businessInfo.bis_country}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Ventas recientes */}
                  <SellerRecentSales sellerId={user.id} />
                </div>
                
                {/* Estadísticas de ventas - ahora debajo de la sección superior */}
                <div className="stats-section">
                  {assignedPointOfSale && (
                    <SellerSalesStats pointOfSaleId={assignedPointOfSale.id} />
                  )}
                </div>
                
                {/* Gráficas de ventas diarias para vendedor */}
                {assignedPointOfSale && (
                  <div className="charts-container">
                    <h2>Gráficas de Ventas</h2>
                    <SellerSalesChart sellerId={user.id} />
                  </div>
                )}
                
                {/* Tabla de gastos mensuales */}
                {assignedPointOfSale && (
                  <SellerMonthlyExpenses />
                )}
                
                {/* La sección de Productos Más Vendidos ha sido eliminada */}
              </>
            ) : null}
          </>
        ) : (
          <div className="no-pos-message">
            <p>No tienes un punto de venta asignado. Por favor, contacta al administrador.</p>
          </div>
        )}
      </div>
    </div>
    
    {/* Asistente IA - Fuera del contenedor para position fixed */}
    <ChatWidget />
    </>
  );
};

export default SellerDashboard;
